"use client";

import { motion } from "framer-motion";
import { t } from "i18next";
import Image from "next/image";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import AutosizeInput from "react-input-autosize";

import { Card } from "../atoms/Card";
import SkeletonLoader from "../molecules/SkeletonLoader";
import { useWalletInfo } from "contexts/WalletInfoContext";
import { useMarinadeState } from "hooks/useMarinadeState";
import Images from "styles/image-library";
import { PositionType } from "types/wallet-position";
import { format2Dec } from "utils/number-to-short-version";

const formatNumberString = (numString: string) => {
  const cleanNumString = numString.replace(/^0+(?=\d)/, "").replace(/,/g, "");

  const parts = cleanNumString.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return parts.join(".");
};

interface ActionInputCardProps {
  isLoading: boolean;
  inputIcon?: string;
  inputTitle?: string;
  inputBalance: number;
  inputType?: PositionType;
  onInputChange: (input: number) => void;
}

export const ActionInputCard: FC<ActionInputCardProps> = ({
  isLoading,
  inputIcon,
  inputTitle,
  inputBalance,
  inputType,
  onInputChange,
}) => {
  const marinadeState = useMarinadeState();
  const { walletAddress } = useWalletInfo();

  const [stringInputAmount, setInputAmount] = useState("");
  const inputAmount = useMemo(
    () => Number(stringInputAmount.replace(/,/g, "")),
    [stringInputAmount]
  );

  useEffect(() => {
    if (!walletAddress) {
      setInputAmount("");
    }
  }, [walletAddress]);

  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  const formatAndSetInput = (amount: string) => {
    const input = Number(amount.replace(/,/g, ""));
    const formattedInput = formatNumberString(amount);
    if (
      !Number.isNaN(input) &&
      formattedInput.match(/^((\d{1,3}(,\d{3})*|\d+)(\.\d{0,9})?)?$/) &&
      Number.MAX_SAFE_INTEGER >= input
    ) {
      setInputAmount(formattedInput);
    }
  };

  useEffect(() => {
    if (timer !== null) {
      clearTimeout(timer);
    }

    const newTimer = setTimeout(() => {
      onInputChange(inputAmount);
    }, 300);

    setTimer(newTimer);

    // Cleanup on unmount or if effect reruns
    return () => {
      clearTimeout(newTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputAmount]);

  const setRef = useCallback((node: HTMLInputElement | null) => {
    if (node) {
      node.focus();
    }
  }, []);

  const inputFontSize = useMemo(() => {
    let fontSize = "text-5xl sm:text-6xl";
    if (stringInputAmount.length > 25) {
      fontSize = "text-lg sm:text-3xl";
    } else if (stringInputAmount.length > 20) {
      fontSize = "text-xl sm:text-3xl";
    } else if (stringInputAmount.length > 16) {
      fontSize = "text-2xl sm:text-4xl";
    } else if (stringInputAmount.length > 13) {
      fontSize = "text-3xl sm:text-5xl";
    } else if (stringInputAmount.length > 10) {
      fontSize = "text-4xl sm:text-5xl";
    } else if (stringInputAmount.length > 8) {
      fontSize = "text-4xl sm:text-6xl";
    }
    return fontSize;
  }, [stringInputAmount]);

  const renderInputType = (type?: PositionType) => {
    switch (type) {
      case PositionType.LP:
        return "LP";
      case PositionType.MNDE:
        return "MNDE";
      default:
        return "SOL";
    }
  };

  const printUsdValue = (amount: number) => {
    let value = 0;

    if (inputType === PositionType.LP) {
      value = amount * (marinadeState?.liquidityPool.lpPrice.usd || 0);
    } else if (inputType === PositionType.MNDE) {
      value = amount * (marinadeState?.mndePrice.usd || 0);
    } else {
      value = amount * (marinadeState?.solPrice.usd || 0);
    }

    return format2Dec(value);
  };

  return (
    <Card className="relative font-medium overflow-x-hidden p-6">
      <div className="flex gap-4 items-center">
        <SkeletonLoader
          isLoading={isLoading}
          className="w-10 h-10 rounded-full"
        >
          {inputIcon ? (
            <Image
              src={inputIcon}
              alt="Input asset icon"
              width={48}
              height={48}
              className="w-10 md:w-12 h-10 md:h-12 rounded-full"
            />
          ) : (
            <div className="w-10 md:w-12 h-10 md:h-12 bg-slate-400 rounded-full" />
          )}
        </SkeletonLoader>
        <div className="flex-grow text-base md:text-xl font-semibold mr-16">
          <SkeletonLoader isLoading={isLoading} className="h-5 w-16 mb-2">
            {inputTitle}
          </SkeletonLoader>
          <SkeletonLoader isLoading={isLoading} className="h-5 w-40">
            {!walletAddress ? (
              <span className="text-tertiary-text text-sm md:text-base font-normal block">
                {t("appPage.errors.notConnected")}
              </span>
            ) : (
              <span className="text-tertiary-text text-sm md:text-base font-normal block">
                Balance: {format2Dec(inputBalance)} {renderInputType(inputType)}
              </span>
            )}
          </SkeletonLoader>
        </div>
      </div>
      <div className="mt-6 flex flex-col items-center relative max-w-full overflow-x-clip">
        <div className="relative font-semibold">
          <SkeletonLoader
            isLoading={isLoading}
            className="h-7 w-24 rounded-lg mb-4"
          >
            <AutosizeInput
              // this ensures the AutosizeInput component is re-rendered when the inputFontSize changes making the boundaries fit the input
              key={`autosize-input-${inputFontSize}`}
              type="text"
              placeholder="0.0"
              value={stringInputAmount}
              onChange={(e) => formatAndSetInput(e.target.value)}
              min={0}
              className={`w-full text-center ${inputFontSize}`}
              inputClassName="!outline-none"
              inputMode="decimal"
              inputRef={setRef}
            />
            <span className="absolute top-0 right-0 translate-x-full text-tertiary-text text-xl">
              {inputType === PositionType.MNDE ? "MNDE" : "SOL"}
            </span>
          </SkeletonLoader>
        </div>
        <SkeletonLoader isLoading={isLoading} className="h-4 w-20 rounded">
          <span className="text-tertiary-text">
            ~$
            {printUsdValue(inputAmount)}
          </span>
        </SkeletonLoader>
        <motion.button
          disabled={!walletAddress}
          whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
          className="mt-4 mb-8-px bg-white rounded-full border border-additional-text pt-1 px-3"
          onClick={() => {
            let maxBalance = inputBalance;

            if (inputType === PositionType.Raw) {
              maxBalance -= 0.05;
            }

            formatAndSetInput(maxBalance.toFixed(9).toString());
          }}
        >
          <Image
            src={Images.icons_max}
            height={24}
            width={24}
            className="w-6 h-6"
            alt="max button"
          />
        </motion.button>
      </div>
    </Card>
  );
};
